// Host → iframe types
export const HOST_TO_IFRAME = {
  INIT: 'init',
  UPDATE: 'update',
  THEME: 'theme',
  /** Scene surface only (`/widget-scene`). See `ScenePayload`. */
  SCENE: 'scene',
} as const;

// Iframe → host types
export const IFRAME_TO_HOST = {
  READY: 'ready',
  EVENT: 'event',
  RESIZE: 'resize',
  ERROR: 'error',
  /** Scene surface only — content height, so the host can size the iframe. */
  SCENE_RESIZE: 'sceneResize',
  /** Scene surface only — relayed onto the `tele:feedback` data topic. */
  SCENE_FEEDBACK: 'sceneFeedback',
} as const;

// ── Scene surface ───────────────────────────────────────────────────────
//
// These are ADDITIVE at PROTOCOL_VERSION 1 on purpose. `isValidEnvelope`
// hard-rejects a version mismatch, and partners embed the UNHASHED
// `/embed.js` (self-hosted copies are frozen on their own origin until they
// re-download), so a bump would break every cached widget until the cache
// turned over. Both message loops ignore unknown `type`s silently, and the
// scene surface is a route an older embed.js never loads, so old and new can
// coexist without a version gate. Do not bump for additive message types.

/** Forwarded verbatim from the `ui-engine:scene` data topic. The renderer,
 *  not the transport, decides between the DSL and legacy-JSON shapes. */
export interface ScenePayload {
  type: 'skeleton' | 'scene';
  /** Layout code on skeletons (`'1-2-1'`), absent on scenes. */
  layout?: string;
  /** Pipe-delimited DSL. Always present from ONE_BRAIN; TWO_BRAIN may send
   *  legacy JSON scene fields alongside/instead. */
  dsl?: string;
  [key: string]: unknown;
}

export interface SceneResizePayload {
  /** Rendered content height in CSS px. */
  height: number;
}

/** Mirrors hosted sites' `informTele`: when the model asks for a layout or a
 *  card the renderer cannot honour, tell the agent so it self-corrects on the
 *  next turn instead of repeating the mistake all conversation. The host
 *  relays this onto `tele:feedback` because the LiveKit room lives in
 *  embed.js, not in the scene iframe. */
export interface SceneFeedbackPayload {
  reason: 'unknown-card-type' | 'layout-mismatch' | 'unsupported-layout';
  detail: string;
}

export interface InitPayload {
  protocolVersion: number;
  position: string;
  config: { toolCalls: boolean; citations: boolean };
}

export interface UpdatePayload {
  agentName?: string | null;
  agentState?: 'listening' | 'thinking' | 'speaking' | 'initializing';
  transcripts?: Array<{ id: string; text: string; participant: string; isFinal: boolean }>;
  toolActivity?: Array<unknown>;
  citations?: Array<unknown>;
  isMuted?: boolean;
  isVolumeMuted?: boolean;
  isConnected?: boolean;
  isOverlayExpanded?: boolean;
  error?: { code: string; message: string } | null;
  micWarning?: string | null;
  avatarEnabled?: boolean;
  avatarVisible?: boolean;
  avatarThumbnailUrl?: string | null;
  avatarInternalMount?: boolean;
  chatMode?: boolean;
}

export type PresentationEvent =
  | { type: 'toggleExpand' }
  | { type: 'toggleMute' }
  | { type: 'toggleVolume' }
  /** Fullscreen layout's eye icon (Phase 6) — flips `avatarVisible` client-side,
   * same flag ChatPanel's Phase 2 crossfade already reads. Not emitted by the
   * compact `ChatPanel`/`Controls` today, so panel-mode behavior is unchanged. */
  | { type: 'toggleAvatarVisible' }
  | { type: 'sendText'; text: string }
  | { type: 'startConversation'; text: string }
  | { type: 'endCall' }
  /** Fullscreen layout's red X. "Close" has always read to users as "end
   * this", so it does both: disconnect AND collapse. `endCall` alone leaves
   * the panel open (correct for the compact panel, which shows a Connect
   * button in its place); `toggleExpand` alone was the old behaviour and is
   * what shipped the "disconnect doesn't work, the avatar is still engaged"
   * bug — the room, the mic track and the avatar all stayed live behind a
   * hidden panel. */
  | { type: 'close' }
  | { type: 'connect' };
